import { useCallback, useEffect, useState } from 'react';
import { misesÀJour } from '../api.js';
import type {
  NatureMiseÀJour,
  Préchecks,
  RunMiseÀJour,
  VersionsDisponibles,
  ÉtatMiseÀJour,
} from '../api.js';
import { durée, ilYA } from '../format.js';
import {
  attendRedémarrage,
  avancement,
  estBloquant,
  peutRevenirEnArrière,
} from './suivi-mise-a-jour.js';

interface Props {
  organizationId: string;
  siteId: string;
  siteNom: string;
  rôle: string;
  /** Consigne de redémarrage du site, quand il ne se relance pas depuis Vigie. */
  consigneRedémarrage?: string | null;
  onRetour: () => void;
}

const LIBELLÉS_ÉTAT: Record<ÉtatMiseÀJour, string> = {
  pending_approval: 'en attente d’approbation',
  queued: 'en file',
  prechecks: 'vérifications',
  backing_up: 'sauvegarde',
  updating: 'mise à jour',
  awaiting_restart: 'redémarrage à faire',
  verifying: 'contrôle du site',
  success: 'réussie',
  failed: 'en échec',
  rolled_back: 'revenue en arrière',
  interrupted: 'interrompue',
};

const LIBELLÉS_NATURE: Record<NatureMiseÀJour, string> = {
  major: 'majeure',
  minor: 'mineure',
  patch: 'correctif',
  none: 'à jour',
};

/**
 * Mises à jour d'un site.
 *
 * L'écran s'organise autour de trois refus, tous hérités de ce que l'orchestrateur garantit déjà :
 *
 * - une majeure ne part jamais d'un clic distrait : elle est annoncée comme telle, et le serveur la
 *   met de toute façon en attente d'approbation, y compris sous la politique automatique ;
 * - un run interrompu bloque le site, et ce blocage est affiché en clair — sans quoi un site
 *   cesserait de recevoir ses correctifs sans que personne ne s'en aperçoive ;
 * - l'avancement n'est montré que lorsqu'il est connu. Pour un run en échec, la base ne conserve
 *   pas l'étape fautive : on renvoie au journal plutôt que d'inventer une position.
 */
export function MisesÀJour({
  organizationId,
  siteId,
  siteNom,
  rôle,
  consigneRedémarrage,
  onRetour,
}: Props) {
  const peutOpérer = rôle === 'admin' || rôle === 'operator';
  const peutDécider = rôle === 'admin';

  const [versions, setVersions] = useState<VersionsDisponibles | null>(null);
  const [runs, setRuns] = useState<RunMiseÀJour[] | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);
  const [avertissement, setAvertissement] = useState<string | null>(null);
  const [enCours, setEnCours] = useState<string | null>(null);
  const [préchecks, setPréchecks] = useState<Préchecks | null>(null);
  const [cible, setCible] = useState<string | null>(null);
  const [motifs, setMotifs] = useState<Record<string, string>>({});

  const charger = useCallback(async () => {
    try {
      const [dispo, historique] = await Promise.all([
        misesÀJour.disponibles(organizationId, siteId),
        misesÀJour.runs(organizationId, siteId),
      ]);

      if (historique.status >= 400) setErreur(historique.data.error ?? 'Historique indisponible');
      else setRuns(historique.data.runs);

      if (dispo.status >= 400) {
        // Une version courante inconnue (409) ou un GitHub injoignable (502) n'empêchent pas de
        // consulter l'historique : on le dit sans masquer le reste de l'écran.
        setAvertissement(dispo.data.error ?? 'Versions disponibles indéterminées');
        setVersions(null);
      } else {
        setAvertissement(null);
        setVersions(dispo.data);
      }
    } catch {
      setErreur('Service injoignable');
    }
  }, [organizationId, siteId]);

  useEffect(() => {
    void charger();
  }, [charger]);

  // Un run en vol progresse côté serveur : sans rafraîchissement, l'écran resterait figé sur
  // « sauvegarde » jusqu'à ce qu'on recharge la page.
  const unEnVol = runs?.some((run) => estBloquant(run.status) && run.status !== 'interrupted');
  useEffect(() => {
    if (unEnVol !== true) return;
    const minuterie = setInterval(() => void charger(), 10_000);
    return () => clearInterval(minuterie);
  }, [unEnVol, charger]);

  async function vérifier(version: string) {
    setEnCours('préchecks');
    setErreur(null);
    setCible(version);
    setPréchecks(null);
    try {
      const { status, data } = await misesÀJour.préchecks(organizationId, siteId, version);
      if (status >= 400) setErreur(data.error ?? 'Vérifications impossibles');
      else setPréchecks(data);
    } catch {
      setErreur('Service injoignable');
    } finally {
      setEnCours(null);
    }
  }

  async function lancer(version: string) {
    setEnCours('lancement');
    setErreur(null);
    try {
      const { status, data } = await misesÀJour.lancer(organizationId, siteId, version);
      if (status >= 400) setErreur(data.error ?? 'Lancement impossible');
      else {
        setPréchecks(null);
        await charger();
      }
    } catch {
      setErreur('Service injoignable');
    } finally {
      setEnCours(null);
    }
  }

  async function agirSurRun(
    runId: string,
    action: 'approuver' | 'revenirEnArrière' | 'abandonner' | 'validerRedémarrage',
  ) {
    setEnCours(runId);
    setErreur(null);
    try {
      const motif = motifs[runId] ?? '';
      const { status, data } =
        action === 'approuver'
          ? await misesÀJour.approuver(organizationId, siteId, runId)
          : action === 'validerRedémarrage'
            ? await misesÀJour.validerRedémarrage(organizationId, siteId, runId)
            : action === 'revenirEnArrière'
              ? await misesÀJour.revenirEnArrière(organizationId, siteId, runId, motif)
              : await misesÀJour.abandonner(organizationId, siteId, runId, motif);

      if (status >= 400) setErreur(data.error ?? 'Opération impossible');
      else {
        setMotifs((précédents) => ({ ...précédents, [runId]: '' }));
        await charger();
      }
    } catch {
      setErreur('Service injoignable');
    } finally {
      setEnCours(null);
    }
  }

  if (erreur !== null && runs === null) return <p className="erreur">{erreur}</p>;
  if (runs === null) return <p className="aide">Chargement des mises à jour…</p>;

  const bloquant = runs.find((run) => estBloquant(run.status));
  const interrompu = runs.find((run) => run.status === 'interrupted');

  /** Propositions de mise à jour, sans doublon si les deux versions coïncident. */
  const propositions = (
    versions === null
      ? []
      : [
          ...(versions.latestWithinMajor === null
            ? []
            : [{ ...versions.latestWithinMajor, étiquette: 'Dernière de la majeure courante' }]),
          ...(versions.latest === null
            ? []
            : [{ ...versions.latest, étiquette: 'Dernière version publiée' }]),
        ]
  ).filter(
    (proposition, index, toutes) =>
      proposition.kind !== 'none' &&
      toutes.findIndex((autre) => autre.version === proposition.version) === index,
  );

  return (
    <section className="carte">
      <button type="button" className="lien" onClick={onRetour}>
        ← Retour au site
      </button>
      <h2>Mises à jour — {siteNom}</h2>

      {erreur !== null && <p className="erreur">{erreur}</p>}
      {avertissement !== null && <p className="avertissement">{avertissement}</p>}

      {versions !== null && (
        <p className="aide">
          Version installée : <strong>{versions.currentVersion}</strong>
        </p>
      )}

      {interrompu !== undefined && (
        <p className="avertissement">
          Une mise à jour a été interrompue le{' '}
          {new Date(interrompu.startedAt).toLocaleString('fr-FR')} : ce site ne recevra plus aucune
          mise à jour tant que ce run n’est pas clos. L’installation a pu rester à moitié modifiée —
          vérifie-la, puis reviens en arrière ou abandonne le run ci-dessous.
        </p>
      )}

      {propositions.length === 0 ? (
        versions !== null && <p className="succes">Ce site est à jour.</p>
      ) : (
        <>
          <h3>Versions disponibles</h3>
          <ul className="liste-versions">
            {propositions.map((proposition) => (
              <li key={proposition.version}>
                <div className="version-entete">
                  <strong>{proposition.version}</strong>
                  <span
                    className={
                      proposition.kind === 'major' ? 'etiquette etiquette--alerte' : 'etiquette'
                    }
                  >
                    {LIBELLÉS_NATURE[proposition.kind]}
                  </span>
                  <span className="aide">{proposition.étiquette}</span>
                </div>

                {proposition.kind === 'major' && (
                  <p className="avertissement">
                    Une version majeure peut demander une version de Node plus récente et casser des
                    thèmes ou des intégrations. Elle n’est jamais appliquée automatiquement : le
                    lancement crée une demande d’approbation.
                  </p>
                )}

                {peutOpérer && (
                  <div className="actions actions--serrees">
                    <button
                      type="button"
                      className="lien"
                      disabled={enCours !== null}
                      onClick={() => void vérifier(proposition.version)}
                    >
                      {enCours === 'préchecks' && cible === proposition.version
                        ? 'Vérification…'
                        : 'Vérifier'}
                    </button>
                    <button
                      type="button"
                      disabled={enCours !== null || bloquant !== undefined}
                      onClick={() => void lancer(proposition.version)}
                    >
                      {enCours === 'lancement' ? 'Mise en file…' : 'Mettre à jour'}
                    </button>
                    {bloquant !== undefined && (
                      <span className="aide">
                        Une mise à jour est déjà {LIBELLÉS_ÉTAT[bloquant.status]}.
                      </span>
                    )}
                  </div>
                )}
              </li>
            ))}
          </ul>
        </>
      )}

      {préchecks !== null && (
        <div className="carte-interne">
          <h3>Vérifications préalables — {cible}</h3>
          {préchecks.ok ? (
            <p className="succes">
              Tout est en ordre : compatibilité, espace disque, état du site et absence d’opération
              concurrente.
            </p>
          ) : (
            <>
              <p className="erreur">
                La mise à jour serait refusée. Toutes les vérifications ont été menées, pour ne pas
                les découvrir une par une :
              </p>
              <ul className="liste-problemes">
                {préchecks.failures.map((échec) => (
                  <li key={échec}>{échec}</li>
                ))}
              </ul>
            </>
          )}
          {Object.keys(préchecks.details).length > 0 && (
            <dl className="fiche">
              {Object.entries(préchecks.details).map(([clé, valeur]) => (
                <div key={clé}>
                  <dt>{clé}</dt>
                  <dd>{valeur}</dd>
                </div>
              ))}
            </dl>
          )}
        </div>
      )}

      <h3>Historique</h3>
      {runs.length === 0 ? (
        <p className="aide">Aucune mise à jour lancée depuis Vigie sur ce site.</p>
      ) : (
        <ul className="liste-runs">
          {runs.map((run) => {
            const étapes = avancement(run.status);
            return (
              <li key={run.id} className={`run run--${run.status}`}>
                <div className="run-entete">
                  <span>
                    <strong>{run.fromVersion || '?'}</strong> → <strong>{run.toVersion}</strong>
                  </span>
                  <span
                    className={
                      run.status === 'failed' || run.status === 'interrupted'
                        ? 'etiquette etiquette--alerte'
                        : 'etiquette'
                    }
                  >
                    {LIBELLÉS_ÉTAT[run.status]}
                  </span>
                  {run.rolledBack && <span className="etiquette">retour arrière effectué</span>}
                  {run.batchId !== null && <span className="etiquette">groupée</span>}
                  <span className="aide">
                    {ilYA(run.startedAt)} · {durée(run.startedAt, run.finishedAt)}
                  </span>
                </div>

                {étapes.length > 0 && (
                  <ol className="etapes">
                    {étapes.map((étape) => (
                      <li key={étape.clé} className={`etape etape--${étape.état}`}>
                        {étape.libellé}
                      </li>
                    ))}
                  </ol>
                )}

                {peutDécider && run.status === 'pending_approval' && (
                  <div className="actions actions--serrees">
                    <button
                      type="button"
                      disabled={enCours !== null}
                      onClick={() => void agirSurRun(run.id, 'approuver')}
                    >
                      Approuver cette majeure
                    </button>
                  </div>
                )}

                {attendRedémarrage(run.status) && (
                  <div className="run-decision">
                    <p className="avertissement">
                      Version {run.toVersion} installée, mais le site tourne encore en{' '}
                      {run.fromVersion} : sur cet hébergement, le redémarrage se fait hors de Vigie.
                    </p>
                    <p className="aide">
                      {consigneRedémarrage ??
                        'Redémarre l’application depuis le panneau de ton hébergeur.'}{' '}
                      Puis valide ci-dessous : Vigie vérifiera que le site répond bien dans la
                      nouvelle version — elle ne prend pas la déclaration pour argent comptant.
                    </p>
                    <div className="actions actions--serrees">
                      <button
                        type="button"
                        disabled={enCours !== null}
                        onClick={() => void agirSurRun(run.id, 'validerRedémarrage')}
                      >
                        {enCours === run.id ? 'Vérification…' : 'J’ai redémarré, vérifier'}
                      </button>
                    </div>
                  </div>
                )}

                {peutDécider &&
                  (peutRevenirEnArrière(run.status, run.rolledBack) ||
                    run.status === 'interrupted') && (
                    <div className="run-decision">
                      <label htmlFor={`motif-${run.id}`}>
                        Motif (consigné dans l’audit, 3 caractères minimum)
                      </label>
                      <input
                        id={`motif-${run.id}`}
                        type="text"
                        value={motifs[run.id] ?? ''}
                        onChange={(événement) =>
                          setMotifs((précédents) => ({
                            ...précédents,
                            [run.id]: événement.target.value,
                          }))
                        }
                      />
                      <div className="actions actions--serrees">
                        {peutRevenirEnArrière(run.status, run.rolledBack) && (
                          <button
                            type="button"
                            disabled={enCours !== null || (motifs[run.id] ?? '').trim().length < 3}
                            onClick={() => void agirSurRun(run.id, 'revenirEnArrière')}
                          >
                            {enCours === run.id ? 'Opération…' : 'Revenir en arrière'}
                          </button>
                        )}
                        {run.status === 'interrupted' && (
                          <button
                            type="button"
                            className="lien"
                            disabled={enCours !== null || (motifs[run.id] ?? '').trim().length < 3}
                            onClick={() => void agirSurRun(run.id, 'abandonner')}
                          >
                            Clore sans revenir en arrière
                          </button>
                        )}
                      </div>
                      {run.status === 'interrupted' && (
                        <p className="aide">
                          Clore le run débloque le site sans rien modifier sur le serveur : à
                          n’utiliser qu’après avoir vérifié que l’installation est cohérente.
                        </p>
                      )}
                    </div>
                  )}

                {run.log !== '' && (
                  <details>
                    <summary>Journal</summary>
                    <pre className="journal">{run.log}</pre>
                  </details>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}
